import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiSave, FiX, FiArrowLeft, FiUser,
    FiBook, FiMapPin, FiFileText,
} from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import admissionService from "../../services/admissionService";
import courseService from "../../services/courseService";

const SOURCES = ["Walk-in", "Referral", "Online", "Phone", "Other"];
const GENDERS = ["Male", "Female", "Other"];
const CATEGORIES = ["General", "OBC", "SC", "ST", "NT", "EWS", "Other"];
const ADM_CATS = ["Merit", "Management", "Lateral Entry", "Other"];

const STAGE_ORDER = { Enquiry: 0, Applied: 1, Admitted: 2, Enrolled: 3, Rejected: 9, Withdrawn: 9 };

function Section({ title, icon: Icon, children, color = "var(--primary)" }) {
    return (
        <div className="card">
            <div className="card-header" style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                <Icon size={13} color={color} /> {title}
            </div>
            <div className="card-body">
                <div className="form-grid">{children}</div>
            </div>
        </div>
    );
}

function Field({ label, required, children, span = 1 }) {
    return (
        <div className="form-group" style={span === 2 ? { gridColumn: "1 / -1" } : {}}>
            <label className="form-label">
                {label}{required && <span style={{ color: "var(--danger)" }}> *</span>}
            </label>
            {children}
        </div>
    );
}

export default function AdmissionForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { academicYear } = useApp();
    const isEdit = Boolean(id);

    const [form, setForm] = useState({
        fullName: "", mobile: "", email: "",
        courseInterested: "", source: "Walk-in",
        enquiryDate: new Date().toISOString().slice(0, 10),
        enquiryNotes: "",
        // Application
        applicationDate: "", gender: "", dob: "",
        address: "", city: "", state: "Maharashtra", pincode: "",
        fatherName: "", motherName: "", parentMobile: "",
        previousInstitution: "", previousPercentage: "", entranceScore: "",
        category: "General", applicationNotes: "",
        // Admission
        admissionDate: "", admissionCategory: "", seatNumber: "", admissionNotes: "",
        // Enrolled
        enrolledDate: "", enrollmentNotes: "",
    });
    const [stage, setStage] = useState("Enquiry");
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEdit);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        courseService.getAll({ isActive: true })
            .then((res) => setCourses(res.data || []))
            .catch((err) => toast.error(err.response?.data?.message || err.message));
    }, []);

    useEffect(() => {
        if (!isEdit) return;
        setFetching(true);
        admissionService.getById(id)
            .then((res) => {
                const d = res.data;
                setStage(d.stage || "Enquiry");
                setForm({
                    fullName: d.fullName || "",
                    mobile: d.mobile || "",
                    email: d.email || "",
                    courseInterested: d.courseInterested || "",
                    source: d.source || "Walk-in",
                    enquiryDate: d.enquiryDate || "",
                    enquiryNotes: d.enquiryNotes || "",
                    applicationDate: d.applicationDate || "",
                    gender: d.gender || "",
                    dob: d.dob || "",
                    address: d.address || "",
                    city: d.city || "",
                    state: d.state || "Maharashtra",
                    pincode: d.pincode || "",
                    fatherName: d.fatherName || "",
                    motherName: d.motherName || "",
                    parentMobile: d.parentMobile || "",
                    previousInstitution: d.previousInstitution || "",
                    previousPercentage: d.previousPercentage ?? "",
                    entranceScore: d.entranceScore ?? "",
                    category: d.category || "General",
                    applicationNotes: d.applicationNotes || "",
                    admissionDate: d.admissionDate || "",
                    admissionCategory: d.admissionCategory || "",
                    seatNumber: d.seatNumber || "",
                    admissionNotes: d.admissionNotes || "",
                    enrolledDate: d.enrolledDate || "",
                    enrollmentNotes: d.enrollmentNotes || "",
                });
            })
            .catch((err) => { toast.error(err.message); navigate("/admissions"); })
            .finally(() => setFetching(false));
    }, [id, isEdit, navigate]);

    const set = (k, v) => {
        setForm((p) => ({ ...p, [k]: v }));
        setErrors((p) => ({ ...p, [k]: "" }));
    };

    const validate = () => {
        const errs = {};
        if (!form.fullName.trim()) errs.fullName = "Name is required.";
        if (!form.mobile.trim()) errs.mobile = "Mobile is required.";
        if (!/^\d{10}$/.test(form.mobile)) errs.mobile = "Mobile must be 10 digits.";
        if (!form.courseInterested) errs.courseInterested = "Course is required.";
        if (!isEdit && !academicYear?._id) errs._ay = "Select an academic year first.";
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setLoading(true);
        try {
            const payload = { ...form };
            // Coerce number fields
            if (payload.previousPercentage !== "") payload.previousPercentage = Number(payload.previousPercentage);
            else payload.previousPercentage = null;
            if (payload.entranceScore !== "") payload.entranceScore = Number(payload.entranceScore);
            else payload.entranceScore = null;

            if (isEdit) {
                await admissionService.update(id, payload);
                toast.success("Admission record updated.");
            } else {
                await admissionService.create({ ...payload, academicYear: academicYear._id });
                toast.success(`Enquiry created for ${form.fullName}.`);
            }
            navigate("/admissions");
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    };

    const stageOrder = STAGE_ORDER[stage] ?? 0;
    const showApplication = stageOrder >= 1;
    const showAdmission = stageOrder >= 2;
    const showEnrolled = stageOrder >= 3;
    const courseOptions = courses.some((course) => course.shortName === form.courseInterested)
        ? courses
        : form.courseInterested
            ? [{ _id: "current", shortName: form.courseInterested }, ...courses]
            : courses;

    if (fetching) return <div className="loading-center"><div className="spinner" />Loading…</div>;

    return (
        <div>
            {/* Header */}
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                <div>
                    <h1 className="page-title">{isEdit ? "Edit Admission" : "New Enquiry"}</h1>
                    <p className="page-subtitle">
                        {isEdit ? `Stage: ${stage}` : (academicYear?.label || "No academic year selected")}
                    </p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button type="button" className="btn btn-outline" onClick={() => navigate("/admissions")}
                        style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                        <FiArrowLeft size={14} /> Back
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={loading}
                        style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                        <FiSave size={14} /> {loading ? "Saving…" : isEdit ? "Update" : "Create Enquiry"}
                    </button>
                </div>
            </div>

            {errors._ay && (
                <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "0.75rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#92400e" }}>
                    {errors._ay}
                </div>
            )}

            <form onSubmit={handleSubmit} style={{ display: "grid", gap: "1rem" }}>

                {/* ── Enquiry Details ── */}
                <Section title="Enquiry Details" icon={FiUser} color="#003b67">
                    <Field label="Full Name" required>
                        <input className={`form-control ${errors.fullName ? "error" : ""}`}
                            value={form.fullName} onChange={(e) => set("fullName", e.target.value)}
                            placeholder="Student's full name" autoFocus />
                        {errors.fullName && <span className="form-error">{errors.fullName}</span>}
                    </Field>

                    <Field label="Mobile Number" required>
                        <input type="tel" maxLength={10} className={`form-control ${errors.mobile ? "error" : ""}`}
                            value={form.mobile} onChange={(e) => set("mobile", e.target.value)}
                            placeholder="10-digit mobile" />
                        {errors.mobile && <span className="form-error">{errors.mobile}</span>}
                    </Field>

                    <Field label="Email">
                        <input type="email" className="form-control"
                            value={form.email} onChange={(e) => set("email", e.target.value)}
                            placeholder="optional" />
                    </Field>

                    <Field label="Course Interested" required>
                        <select className={`form-control ${errors.courseInterested ? "error" : ""}`}
                            value={form.courseInterested} onChange={(e) => set("courseInterested", e.target.value)}>
                            <option value="">Select course</option>
                            {courseOptions.map((course) => (
                                <option key={course._id} value={course.shortName}>
                                    {course.shortName}
                                </option>
                            ))}
                        </select>
                        {courses.length === 0 && (
                            <span className="form-hint">Add an active course under Settings first.</span>
                        )}
                        {errors.courseInterested && <span className="form-error">{errors.courseInterested}</span>}
                    </Field>

                    <Field label="Source">
                        <select className="form-control" value={form.source} onChange={(e) => set("source", e.target.value)}>
                            {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </Field>

                    <Field label="Enquiry Date">
                        <input type="date" className="form-control"
                            value={form.enquiryDate} onChange={(e) => set("enquiryDate", e.target.value)} />
                    </Field>

                    <Field label="Notes" span={2}>
                        <textarea rows={2} className="form-control" style={{ resize: "vertical" }}
                            value={form.enquiryNotes} onChange={(e) => set("enquiryNotes", e.target.value)}
                            placeholder="Any remarks from initial enquiry…" />
                    </Field>
                </Section>

                {/* ── Application Details (stage >= Applied) ── */}
                {showApplication && (
                    <Section title="Application Details" icon={FiFileText} color="#d97706">
                        <Field label="Application Date">
                            <input type="date" className="form-control"
                                value={form.applicationDate} onChange={(e) => set("applicationDate", e.target.value)} />
                        </Field>
                        <Field label="Gender">
                            <select className="form-control" value={form.gender} onChange={(e) => set("gender", e.target.value)}>
                                <option value="">Select…</option>
                                {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
                            </select>
                        </Field>
                        <Field label="Date of Birth">
                            <input type="date" className="form-control"
                                value={form.dob} onChange={(e) => set("dob", e.target.value)} />
                        </Field>
                        <Field label="Category">
                            <select className="form-control" value={form.category} onChange={(e) => set("category", e.target.value)}>
                                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </Field>
                        <Field label="Previous Percentage">
                            <input type="number" min="0" max="100" className="form-control"
                                value={form.previousPercentage} onChange={(e) => set("previousPercentage", e.target.value)}
                                placeholder="e.g. 75.5" />
                        </Field>
                        <Field label="Entrance Score">
                            <input type="number" min="0" className="form-control"
                                value={form.entranceScore} onChange={(e) => set("entranceScore", e.target.value)}
                                placeholder="optional" />
                        </Field>
                        <Field label="Father's Name">
                            <input className="form-control" value={form.fatherName}
                                onChange={(e) => set("fatherName", e.target.value)} />
                        </Field>
                        <Field label="Mother's Name">
                            <input className="form-control" value={form.motherName}
                                onChange={(e) => set("motherName", e.target.value)} />
                        </Field>
                        <Field label="Parent Mobile">
                            <input type="tel" maxLength={10} className="form-control"
                                value={form.parentMobile} onChange={(e) => set("parentMobile", e.target.value)}
                                placeholder="10 digits" />
                        </Field>
                        <Field label="Previous Institution">
                            <input className="form-control" value={form.previousInstitution}
                                onChange={(e) => set("previousInstitution", e.target.value)}
                                placeholder="School / college name" />
                        </Field>
                        <Field label="Notes" span={2}>
                            <textarea rows={2} className="form-control" style={{ resize: "vertical" }}
                                value={form.applicationNotes} onChange={(e) => set("applicationNotes", e.target.value)} />
                        </Field>
                    </Section>
                )}

                {/* ── Address (stage >= Applied) ── */}
                {showApplication && (
                    <Section title="Address" icon={FiMapPin} color="#7c3aed">
                        <Field label="Address" span={2}>
                            <input className="form-control" value={form.address}
                                onChange={(e) => set("address", e.target.value)} placeholder="Street / locality" />
                        </Field>
                        <Field label="City">
                            <input className="form-control" value={form.city}
                                onChange={(e) => set("city", e.target.value)} />
                        </Field>
                        <Field label="State">
                            <input className="form-control" value={form.state}
                                onChange={(e) => set("state", e.target.value)} />
                        </Field>
                        <Field label="Pincode">
                            <input className="form-control" maxLength={6} value={form.pincode}
                                onChange={(e) => set("pincode", e.target.value)} />
                        </Field>
                    </Section>
                )}

                {/* ── Admission Details (stage >= Admitted) ── */}
                {showAdmission && (
                    <Section title="Admission Details" icon={FiBook} color="#7c3aed">
                        <Field label="Admission Date">
                            <input type="date" className="form-control"
                                value={form.admissionDate} onChange={(e) => set("admissionDate", e.target.value)} />
                        </Field>
                        <Field label="Admission Category">
                            <select className="form-control" value={form.admissionCategory}
                                onChange={(e) => set("admissionCategory", e.target.value)}>
                                <option value="">Select…</option>
                                {ADM_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </Field>
                        <Field label="Seat Number">
                            <input className="form-control" value={form.seatNumber}
                                onChange={(e) => set("seatNumber", e.target.value)} placeholder="optional" />
                        </Field>
                        <Field label="Admission Notes" span={2}>
                            <textarea rows={2} className="form-control" style={{ resize: "vertical" }}
                                value={form.admissionNotes} onChange={(e) => set("admissionNotes", e.target.value)} />
                        </Field>
                    </Section>
                )}

                {/* ── Enrollment Details (stage = Enrolled) ── */}
                {showEnrolled && (
                    <Section title="Enrollment Details" icon={FiBook} color="#16a34a">
                        <Field label="Enrolled Date">
                            <input type="date" className="form-control"
                                value={form.enrolledDate} onChange={(e) => set("enrolledDate", e.target.value)} />
                        </Field>
                        <Field label="Enrollment Notes" span={2}>
                            <textarea rows={2} className="form-control" style={{ resize: "vertical" }}
                                value={form.enrollmentNotes} onChange={(e) => set("enrollmentNotes", e.target.value)} />
                        </Field>
                    </Section>
                )}

                {/* Footer actions */}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                    <button type="button" className="btn btn-outline"
                        onClick={() => navigate("/admissions")}
                        style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                        <FiX size={14} /> Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={loading}
                        style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                        <FiSave size={14} /> {loading ? "Saving…" : isEdit ? "Update" : "Create Enquiry"}
                    </button>
                </div>
            </form>
        </div>
    );
}
